import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SearchX, MapPin, BadgeCheck } from "lucide-react";
import api, { useTitle } from "../lib/api";
import SearchWidget from "../components/SearchWidget";
import CompanyCard from "../components/CompanyCard";
import AdSlot from "../components/AdSlot";

export default function SearchPage() {
  const [params] = useSearchParams();
  const date = params.get("date") || "";
  const city = params.get("city") || "";
  const district = params.get("district") || "";
  const category = params.get("category") || "";
  const guests = params.get("guests") || "";
  const meal = params.get("meal") || "";

  useTitle("Arama Sonuçları");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("");

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (date) p.set("date", date);
    if (city) p.set("city", city);
    if (district) p.set("district", district);
    if (category) p.set("category", category);
    if (guests) p.set("guests", guests);
    if (meal) p.set("meal", meal);
    api.get(`/search?${p.toString()}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (category) {
      api.get("/categories").then((r) => {
        const c = r.data.find((x) => x.id === category);
        if (c) setCategoryName(c.name);
      }).catch(() => {});
    } else setCategoryName("");
  }, [date, city, district, category, guests, meal]);

  const dateText = date ? new Date(`${date}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <SearchWidget compact initial={{ date, city, district, category, guests, meal }} />

      <div className="mt-8 mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-white" data-testid="search-results-title">
          {loading ? "Aranıyor..." : `${data?.count ?? 0} firma bulundu`}
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {[dateText, city, district, categoryName, guests ? `${guests} kişi` : "", meal === "yemekli" ? "Yemekli" : meal === "yemeksiz" ? "Yemeksiz" : ""].filter(Boolean).join(" · ")}
        </p>
      </div>

      <AdSlot placement="search_top" city={city} district={district} category={category} className="mb-6" />

      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="card h-48 animate-pulse" />)}</div>
      ) : data?.results?.length ? (
        <div className="space-y-5">
          {data.results.map((item, i) => (
            <Fragment key={item.company.id}>
              <CompanyCard item={item} date={date} />
              {i === 1 && <AdSlot placement="search_mid" city={city} district={district} category={category} />}
            </Fragment>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center" data-testid="search-empty-state">
          <SearchX size={40} className="mx-auto text-slate-600 mb-4" />
          <div className="font-bold text-white text-lg">Sonuç bulunamadı</div>
          <p className="text-sm text-slate-400 mt-2">Farklı bir tarih, ilçe veya kategori deneyin. İlçe seçmeden il genelinde arama yapabilirsiniz.</p>
        </div>
      )}

      <AdSlot placement="search_bottom" city={city} district={district} category={category} className="mt-8" />
    </div>
  );
}

export function DirectoryPage() {
  useTitle("Firmalar");
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-3xl font-bold text-white mb-8">Tüm Firmalar</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {companies.map((c) => (
          <Link key={c.id} to={`/firma/${c.slug}`} data-testid={`directory-company-${c.slug}`}
            className="card overflow-hidden hover:scale-[1.02] hover:border-[#D4AF37]/40 hover:shadow-2xl transition-all duration-300">
            <div className="h-40 relative">
              <img src={c.logo} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
            </div>
            <div className="p-4">
              <div className="font-bold text-slate-100 flex items-center gap-1.5">
                {c.name} {c.verified && <BadgeCheck size={15} className="text-[#D4AF37]" />}
              </div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} /> {c.city} / {c.district}</div>
              <div className="text-xs text-[#D4AF37] font-semibold mt-2">{c.resource_count} kaynak</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
